import { useMemo, useState } from "react";
import { useDataContext } from "../context/DataContext";
import "./Styles/JobsPage.css";

// ============================================================================
// TYPES
// ============================================================================

interface JobInfo {
  jobId: string;
  jobNumber: string;
  start: string;
  end: string;
  workDescription: string;
  clientName: string;
  siteName: string;
  siteInstructions: string;
  siteLocation: string;
}

interface JobWithPersonnel extends JobInfo {
  assignedPersonnel: { id: string; name: string }[];
}

type UnknownRecord = Record<string, unknown>;

// ============================================================================
// UTILITIES
// ============================================================================

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const pickString = (record: UnknownRecord, keys: string[]): string | null => {
  for (const key of keys) {
    const value = record[key];
    if (value === null || value === undefined) continue;
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
  }
  return null;
};

const extractRows = (data: unknown): UnknownRecord[] => {
  if (Array.isArray(data)) {
    return data.filter(isRecord);
  }
  if (isRecord(data)) {
    const possibleArray = data["value"];
    if (Array.isArray(possibleArray)) {
      return possibleArray.filter(isRecord);
    }
    const dataField = data["data"];
    if (Array.isArray(dataField)) {
      return dataField.filter(isRecord);
    }
    if (isRecord(dataField)) {
      const nestedValue = dataField["value"];
      if (Array.isArray(nestedValue)) {
        return nestedValue.filter(isRecord);
      }
    }
    return [data];
  }
  return [];
};

function formatDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return "—";
  
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "—";
    const dd = String(date.getUTCDate()).padStart(2, "0");
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = date.getUTCFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };
  
  const startFormatted = start ? formatDate(start) : "—";
  const endFormatted = end ? formatDate(end) : "—";
  
  if (startFormatted === endFormatted) {
    return startFormatted;
  }
  
  return `${startFormatted} – ${endFormatted}`;
}

function extractJobFromAssignment(assignment: UnknownRecord): JobInfo | null {
  const job = assignment["Job"] as UnknownRecord | undefined;
  if (!job) return null;
  
  const client = job["Client"] as UnknownRecord | undefined;
  const site = job["Site"] as UnknownRecord | undefined;
  
  const jobId = pickString(job, ["JobId"]) ?? "";
  if (!jobId) return null;
  
  return {
    jobId,
    jobNumber: pickString(job, ["JobNumber"]) ?? "—",
    start: pickString(job, ["Start"]) ?? "",
    end: pickString(job, ["End"]) ?? "",
    workDescription: pickString(job, ["WorkDescription"]) ?? "",
    clientName: client ? (pickString(client, ["ClientName"]) ?? "—") : "—",
    siteName: site ? (pickString(site, ["SiteName"]) ?? "—") : "—",
    siteInstructions: site ? (pickString(site, ["Instructions"]) ?? "") : "",
    siteLocation: site ? (pickString(site, ["Location"]) ?? "") : ""
  };
}

// ============================================================================
// NON-SUPERVISOR VIEW
// ============================================================================

function NonSupervisorJobsView({ jobs }: { jobs: JobInfo[] }) {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  
  if (jobs.length === 0) {
    return (
      <div className="jobs-empty">
        <div className="jobs-empty__icon">📋</div>
        <p className="jobs-empty__text">You have no active job assignments.</p>
      </div>
    );
  }
  
  return (
    <div className="jobs-list">
      {jobs.map((job) => {
        const isExpanded = expandedJob === job.jobId;
        return (
          <div key={job.jobId} className="jobs-card">
            <div 
              className="jobs-card__header"
              onClick={() => setExpandedJob(isExpanded ? null : job.jobId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedJob(isExpanded ? null : job.jobId); }}
            >
              <div className="jobs-card__main">
                <span className="jobs-card__number">{job.jobNumber}</span>
                <span className="jobs-card__client">{job.clientName}</span>
              </div>
              <div className="jobs-card__meta">
                <span className="jobs-card__dates">{formatDateRange(job.start, job.end)}</span>
                <span className={`jobs-card__expand-icon${isExpanded ? " jobs-card__expand-icon--expanded" : ""}`}>
                  ▼
                </span>
              </div>
            </div>
            {isExpanded && (
              <div className="jobs-card__details">
                <div className="jobs-detail">
                  <span className="jobs-detail__label">Site</span>
                  <span className="jobs-detail__value">{job.siteName}</span>
                </div>
                {job.siteLocation && (
                  <div className="jobs-detail">
                    <span className="jobs-detail__label">Location</span>
                    <span className="jobs-detail__value">{job.siteLocation}</span>
                  </div>
                )}
                {job.workDescription && (
                  <div className="jobs-detail jobs-detail--full">
                    <span className="jobs-detail__label">Work Description</span>
                    <span className="jobs-detail__value jobs-detail__value--description">{job.workDescription}</span>
                  </div>
                )}
                {job.siteInstructions && (
                  <div className="jobs-detail jobs-detail--full">
                    <span className="jobs-detail__label">Site Instructions</span>
                    <span className="jobs-detail__value jobs-detail__value--instructions">{job.siteInstructions}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// SUPERVISOR VIEW
// ============================================================================

function SupervisorJobsView({ jobs }: { jobs: JobWithPersonnel[] }) {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) return jobs;
    const term = searchTerm.toLowerCase();
    return jobs.filter(job => 
      job.jobNumber.toLowerCase().includes(term) ||
      job.clientName.toLowerCase().includes(term) ||
      job.siteName.toLowerCase().includes(term) ||
      job.workDescription.toLowerCase().includes(term) ||
      job.assignedPersonnel.some(p => p.name.toLowerCase().includes(term))
    );
  }, [jobs, searchTerm]);
  
  if (jobs.length === 0) {
    return (
      <div className="jobs-empty">
        <div className="jobs-empty__icon">📋</div>
        <p className="jobs-empty__text">No active jobs found for you or your supervised personnel.</p>
      </div>
    );
  }
  
  const toggleExpand = (jobId: string) => {
    setExpandedJob(prev => prev === jobId ? null : jobId);
  };
  
  return (
    <>
      <div className="jobs-search">
        <input
          type="text"
          placeholder="Search jobs or personnel..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="jobs-search__input"
        />
      </div>
      
      <div className="jobs-list">
        {filteredJobs.length === 0 ? (
          <div className="jobs-empty jobs-empty--inline">No matching jobs found</div>
        ) : (
          filteredJobs.map((job) => {
            const isExpanded = expandedJob === job.jobId;
            return (
              <div key={job.jobId} className="jobs-card jobs-card--supervisor">
                <div 
                  className="jobs-card__header"
                  onClick={() => toggleExpand(job.jobId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleExpand(job.jobId); }}
                >
                  <div className="jobs-card__main">
                    <div className="jobs-card__title-row">
                      <span className="jobs-card__number">{job.jobNumber}</span>
                      <span className="jobs-personnel-badge">{job.assignedPersonnel.length}</span>
                    </div>
                    <span className="jobs-card__client">{job.clientName}</span>
                    <span className="jobs-card__site">{job.siteName}</span>
                  </div>
                  <div className="jobs-card__meta">
                    <span className="jobs-card__dates">{formatDateRange(job.start, job.end)}</span>
                    <span className={`jobs-card__expand-icon${isExpanded ? " jobs-card__expand-icon--expanded" : ""}`}>
                      ▼
                    </span>
                  </div>
                </div>
                {isExpanded && (
                  <div className="jobs-card__details">
                    {job.siteLocation && (
                      <div className="jobs-detail">
                        <span className="jobs-detail__label">Location</span>
                        <span className="jobs-detail__value">{job.siteLocation}</span>
                      </div>
                    )}
                    {job.workDescription && (
                      <div className="jobs-detail jobs-detail--full">
                        <span className="jobs-detail__label">Work Description</span>
                        <span className="jobs-detail__value jobs-detail__value--description">{job.workDescription}</span>
                      </div>
                    )}
                    {job.siteInstructions && (
                      <div className="jobs-detail jobs-detail--full">
                        <span className="jobs-detail__label">Site Instructions</span>
                        <span className="jobs-detail__value jobs-detail__value--instructions">{job.siteInstructions}</span>
                      </div>
                    )}
                    <div className="jobs-personnel-section">
                      <span className="jobs-personnel-section__title">
                        Assigned Personnel ({job.assignedPersonnel.length})
                      </span>
                      <div className="jobs-personnel-list">
                        {job.assignedPersonnel.map((person) => (
                          <div key={person.id} className="jobs-personnel-item">
                            {person.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function JobsPage(): JSX.Element {
  const { supervisedData, rootAppData, currentPersonnelId, isSupervisor } = useDataContext();
  
  // Extract jobs for non-supervisor view (just the current user's jobs)
  const userJobs = useMemo((): JobInfo[] => {
    if (!rootAppData) return [];
    
    // Get assignments from rootAppData
    let rootAssignments: UnknownRecord[] = [];
    if (isRecord(rootAppData)) {
      const dataField = rootAppData["data"];
      if (isRecord(dataField)) {
        rootAssignments = (dataField["Assignments"] as UnknownRecord[]) ?? [];
      }
      if (rootAssignments.length === 0) {
        rootAssignments = (rootAppData["Assignments"] as UnknownRecord[]) ?? [];
      }
    }
    
    // Also check supervised data for the current user
    const supervisedRows = extractRows(supervisedData);
    const currentUserRow = supervisedRows.find(row => {
      const id = pickString(row, ["PersonnelId", "Id"]);
      return id?.toLowerCase() === currentPersonnelId?.toLowerCase();
    });
    
    if (currentUserRow) {
      const userAssignments = (currentUserRow["Assignments"] as UnknownRecord[]) ?? [];
      rootAssignments = [...rootAssignments, ...userAssignments];
    }
    
    // Extract unique jobs
    const jobMap = new Map<string, JobInfo>();
    rootAssignments.forEach(assignment => {
      const job = extractJobFromAssignment(assignment);
      if (job && !jobMap.has(job.jobId)) {
        jobMap.set(job.jobId, job);
      }
    });
    
    // Sort by start date (most recent first)
    return Array.from(jobMap.values()).sort((a, b) => {
      if (!a.start && !b.start) return 0;
      if (!a.start) return 1;
      if (!b.start) return -1;
      return new Date(b.start).getTime() - new Date(a.start).getTime();
    });
  }, [rootAppData, supervisedData, currentPersonnelId]);
  
  // Extract all jobs with personnel for supervisor view
  const allJobsWithPersonnel = useMemo((): JobWithPersonnel[] => {
    if (!supervisedData) return [];
    
    const supervisedRows = extractRows(supervisedData);
    const jobPersonnelMap = new Map<string, { job: JobInfo; personnel: Map<string, string> }>();
    
    // Process all supervised personnel (including self if present)
    supervisedRows.forEach(row => {
      const personId = pickString(row, ["PersonnelId", "Id"]) ?? "";
      const givenName = pickString(row, ["GivenName", "GivenNames"]) ?? "";
      const surname = pickString(row, ["Surname"]) ?? "";
      const personName = [givenName, surname].filter(Boolean).join(" ").trim() || "Unknown";
      
      const assignments = (row["Assignments"] as UnknownRecord[]) ?? [];
      
      assignments.forEach(assignment => {
        const job = extractJobFromAssignment(assignment);
        if (!job) return;
        
        if (!jobPersonnelMap.has(job.jobId)) {
          jobPersonnelMap.set(job.jobId, { job, personnel: new Map() });
        }
        
        const entry = jobPersonnelMap.get(job.jobId)!;
        if (!entry.personnel.has(personId)) {
          entry.personnel.set(personId, personName);
        }
      });
    });
    
    // Also check rootAppData for the logged-in user's assignments
    if (isRecord(rootAppData)) {
      const dataField = rootAppData["data"];
      let rootAssignments: UnknownRecord[] = [];
      let rootName = "";
      let rootId = currentPersonnelId ?? "";
      
      if (isRecord(dataField)) {
        rootAssignments = (dataField["Assignments"] as UnknownRecord[]) ?? [];
        const givenName = pickString(dataField, ["GivenName", "GivenNames"]) ?? "";
        const surname = pickString(dataField, ["Surname"]) ?? "";
        rootName = [givenName, surname].filter(Boolean).join(" ").trim() || "You";
        rootId = pickString(dataField, ["PersonnelId", "Id"]) ?? rootId;
      }
      
      rootAssignments.forEach(assignment => {
        const job = extractJobFromAssignment(assignment);
        if (!job) return;
        
        if (!jobPersonnelMap.has(job.jobId)) {
          jobPersonnelMap.set(job.jobId, { job, personnel: new Map() });
        }
        
        const entry = jobPersonnelMap.get(job.jobId)!;
        if (!entry.personnel.has(rootId)) {
          entry.personnel.set(rootId, rootName);
        }
      });
    }
    
    // Convert to array with personnel list
    const result: JobWithPersonnel[] = Array.from(jobPersonnelMap.values()).map(({ job, personnel }) => ({
      ...job,
      assignedPersonnel: Array.from(personnel.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    }));
    
    // Sort by start date (most recent first)
    result.sort((a, b) => {
      if (!a.start && !b.start) return 0;
      if (!a.start) return 1;
      if (!b.start) return -1;
      return new Date(b.start).getTime() - new Date(a.start).getTime();
    });
    
    return result;
  }, [supervisedData, rootAppData, currentPersonnelId]);
  
  return (
    <div className="jobs-page">
      
      {isSupervisor ? (
        <SupervisorJobsView jobs={allJobsWithPersonnel} />
      ) : (
        <NonSupervisorJobsView jobs={userJobs} />
      )}
    </div>
  );
}
